import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Minus, Plus, ScanText } from "lucide-react";
import { cn } from "@/lib/utils";
import { api, errMsg } from "@/lib/api";
import { Marks, PdfView, ocrRects, pill, toolBtn, useOcrPages } from "@/components/canvas/PdfView";

/* Small originals (photos) stay as blob URLs between opens – reopening a picture costs nothing. */
const blobCache = new Map();
const BLOB_CACHE_MAX = 12;

/** The original as an authenticated blob URL, cached across opens. */
export function useBlobUrl(caseId, docId) {
  const [url, setUrl] = useState(() => blobCache.get(docId)?.url || null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    const hit = blobCache.get(docId);
    if (hit) { hit.at = Date.now(); setUrl(hit.url); setErr(null); return undefined; }
    setUrl(null);
    setErr(null);
    api.get(`/projects/${caseId}/documents/${docId}/download`, { params: { inline: 1 }, responseType: "blob" })
      .then((r) => {
        const obj = URL.createObjectURL(r.data);
        blobCache.set(docId, { url: obj, at: Date.now() });
        if (blobCache.size > BLOB_CACHE_MAX) { const [k, v] = [...blobCache.entries()].sort((a, b) => a[1].at - b[1].at)[0]; blobCache.delete(k); URL.revokeObjectURL(v.url); }
        if (alive) setUrl(obj);
      })
      .catch((e) => alive && setErr(errMsg(e)));
    return () => { alive = false; };
  }, [caseId, docId]);
  return { url, err };
}

/** A photo or scan: shown as it is, with the OCR-read passage marked when the viewer asked for a text. */
export function ImageView({ caseId, doc, url, queries, compact }) {
  const [zoom, setZoom] = useState(1);
  const q = useMemo(() => (queries || []).filter((x) => x && String(x).trim().length >= 4).map(String), [queries]);
  const { ocr, status, request, run } = useOcrPages({ caseId, docId: doc.id, enabled: true, initialStatus: doc.ocr?.status });
  useEffect(() => { request(1); }, [request]);
  const words = ocr[1]?.words;
  const rects = useMemo(() => (words && q.length ? ocrRects(words, q) : []), [words, q]);
  const busy = ["pending", "running"].includes(status);
  const read = Boolean(words?.length);
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="image-view" data-ocr-status={status || undefined} data-hits={rects.length ? 1 : 0}>
      <div className={cn("flex shrink-0 flex-wrap items-center gap-1 border-b border-black/[0.06] bg-white/90 px-2 backdrop-blur", compact ? "min-h-9" : "min-h-10")} data-testid="image-toolbar">
        <button type="button" onClick={() => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))} disabled={zoom <= 1} className={toolBtn} aria-label="Zoom ut" data-testid="image-zoom-out"><Minus className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={() => setZoom(1)} className="h-7 rounded-[7px] px-1.5 font-mono text-[11.5px] tabular-nums text-foreground/70 hover:bg-black/[0.06]" data-testid="image-zoom-reset">{Math.round(zoom * 100)} %</button>
        <button type="button" onClick={() => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))} className={toolBtn} aria-label="Zoom inn" data-testid="image-zoom-in"><Plus className="h-3.5 w-3.5" /></button>
        <span className="flex-1" />
        {q.length && read ? <span className={cn("px-1 text-[12px]", rects.length ? "text-foreground/80" : "text-muted-foreground")} data-testid="image-hit-count">{rects.length ? "Kildetekst markert" : "Fant ikke teksten i bildet"}</span> : null}
        {busy ? <span className={pill} data-testid="image-ocr-state"><Loader2 className="h-3 w-3 animate-spin" /> Leses med OCR</span>
          : read ? <span className={pill} title={`${words.length} ord lest med OCR`} data-testid="image-ocr-state"><ScanText className="h-3 w-3" /> OCR · {words.length} ord</span>
            : status === "done" ? <span className={pill} data-testid="image-ocr-state"><ScanText className="h-3 w-3" /> Ingen lesbar tekst</span>
              : <button type="button" onClick={run} className={cn(pill, "hover:bg-black/[0.09] hover:text-foreground")} data-testid="image-ocr-run"><ScanText className="h-3 w-3" /> Les med OCR</button>}
      </div>
      <div className="ph-no-capture rr-block min-h-0 flex-1 overflow-auto bg-[hsl(210_20%_94%)] p-4 scrollbar-thin" data-testid="image-scroll">
        <div className="flex min-h-full items-start justify-center">
          <div className="relative inline-block" style={zoom > 1 ? { width: `${zoom * 100}%` } : undefined} data-testid="image-frame">
            <img src={url} alt={doc.name} className={cn("block rounded-[6px] shadow-[0_1px_2px_rgba(0,0,0,.08),0_8px_24px_-16px_rgba(0,0,0,.3)]", zoom > 1 ? "w-full" : "max-h-[calc(100dvh-220px)] max-w-full")} data-testid="source-view-image" />
            <Marks rects={rects} page={1} show />
          </div>
        </div>
      </div>
    </div>
  );
}

/** PDF / image: the PDF streams straight from the API (byte ranges, cached between opens) and opens on the referenced page with the source passage marked. */
export function BlobView({ caseId, doc, page, where, className, compact }) {
  if (doc.kind === "pdf") return <PdfView page={where?.page || page || 1} pages={where?.pages} queries={where?.q} caseId={caseId} docId={doc.id} ocr={doc.ocr} className={className} compact={compact} />;
  return <ImageBlob caseId={caseId} doc={doc} where={where} compact={compact} />;
}

function ImageBlob({ caseId, doc, where, compact }) {
  const { url, err } = useBlobUrl(caseId, doc.id);
  if (err) return <p className="p-6 text-[13px] text-[hsl(var(--v-blocked))]" data-testid="source-view-error">{err}</p>;
  if (!url) return <div className="flex h-full items-center justify-center text-muted-foreground" data-testid="source-view-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  return <ImageView caseId={caseId} doc={doc} url={url} queries={where?.q} compact={compact} />;
}

export function TextView({ caseId, doc }) {
  const [text, setText] = useState(null);
  useEffect(() => { setText(null); api.get(`/projects/${caseId}/documents/${doc.id}/text`).then((r) => setText(r.data.text || "")).catch((e) => setText(errMsg(e))); }, [caseId, doc.id]);
  if (text === null) return <div className="flex h-full items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  return <pre className="h-full overflow-auto whitespace-pre-wrap p-6 font-sans text-[13.5px] leading-relaxed scrollbar-thin" data-testid="source-view-text">{text || "Ingen lesbar tekst i dokumentet."}</pre>;
}

export async function downloadOriginal(caseId, doc, onError) {
  try {
    const r = await api.get(`/projects/${caseId}/documents/${doc.id}/download`, { responseType: "blob" });
    const obj = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = obj; a.download = doc.name; a.click();
    setTimeout(() => URL.revokeObjectURL(obj), 60_000);
  } catch (e) {
    onError?.(errMsg(e));
  }
}
